import {
  CANADIAN_PROVINCE_NAMES,
  type HealthCardFollowUp,
  INTAKE_CHART_ENTRY_ANSWER,
  INTAKE_DEMOGRAPHIC_FIELDS,
  type IntakeChartEntrySection,
  type IntakeDemographicField,
  intakeReasonFitsBooking,
  OHIP_PAYER_NAME,
  type PatientDemographics,
  type PatientQuestionnaireCoverage,
  SCHEDULING_BOOKING_REASON_MAX_LENGTH,
  type SchedulingIntakeAdministrativeAnswers,
  type SchedulingIntakeApplicationKind,
  type SchedulingIntakeClinicalAnswers,
  type SchedulingIntakeSection,
  type SchedulingIntakeSectionReview,
  splitIntakeAnswer,
} from "@emr/contracts";

/*
 * The pure parts of the intake review (PA-6): what the patient answered beside what the chart
 * holds, and which parts of a free-text answer can be added to the chart word for word. Nothing
 * here guesses: a "None" answer is never turned into "no known allergies", and nothing is
 * rewritten before it is applied.
 */

// ---------------------------------------------------------------------------
// Identity and coverage beside the chart
// ---------------------------------------------------------------------------

export const INTAKE_FIELD_LABELS: Readonly<Record<IntakeDemographicField, string>> = {
  legalFirstName: "Legal first name",
  legalMiddleName: "Legal middle name",
  legalLastName: "Legal last name",
  preferredName: "Preferred name",
  administrativeSex: "Sex",
  phone: "Mobile number",
  email: "Email",
  address: "Address",
  emergencyContact: "Emergency contact",
  coverage: "Health coverage",
};

const SEX_LABELS: Readonly<Record<string, string>> = {
  female: "Female",
  male: "Male",
  other: "Other",
  unknown: "Prefer not to say",
};

type Address = {
  readonly line1?: string | undefined;
  readonly line2?: string | undefined;
  readonly city?: string | undefined;
  readonly region?: string | undefined;
  readonly province?: string | undefined;
  readonly state?: string | undefined;
  readonly postalCode?: string | undefined;
  readonly country?: string | undefined;
};
type Contact = {
  readonly name?: string | undefined;
  readonly relationship?: string | undefined;
  readonly phone?: string | undefined;
};

const joined = (parts: readonly (string | undefined)[], separator: string) =>
  parts
    .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
    .map((part) => part.trim())
    .join(separator);

function addressLine(address: Address | undefined): string {
  if (address === undefined) return "";
  return joined(
    [
      address.line1,
      address.line2,
      address.city,
      address.region ?? address.province ?? address.state,
      address.postalCode,
      address.country,
    ],
    ", ",
  );
}

function contactLine(contact: Contact | undefined): string {
  if (contact === undefined) return "";
  return joined([contact.name, contact.relationship, contact.phone], " · ");
}

const digits = (value: string) => value.replace(/\D/gu, "");
const plain = (value: string) => value.trim().replace(/\s+/gu, " ").toLocaleLowerCase();

/** The coverage as staff read it; never shortened, since staff compare it with the card. */
export function questionnaireCoverageLine(coverage: PatientQuestionnaireCoverage): string {
  if (coverage.kind === "cash") return "No provincial or federal coverage";
  if (coverage.payerName === OHIP_PAYER_NAME) {
    return joined(
      [
        `Ontario health card ${coverage.memberId}`,
        "versionCode" in coverage && coverage.versionCode
          ? `version ${coverage.versionCode}`
          : undefined,
        "expiryDate" in coverage && coverage.expiryDate
          ? `expires ${coverage.expiryDate}`
          : undefined,
      ],
      " · ",
    );
  }
  if (coverage.payerKind === "federal_program") {
    return `Interim Federal Health (IFH) client ID ${coverage.memberId}`;
  }
  return `${coverage.payerName} ${coverage.memberId}`;
}

function chartCoverageLine(demographics: PatientDemographics): string {
  const coverage = demographics.coverage;
  if (coverage === undefined) return "";
  return joined(
    [
      `${coverage.payerName} ${coverage.memberId}`,
      coverage.versionCode ? `version ${coverage.versionCode}` : undefined,
      coverage.expiryDate ? `expires ${coverage.expiryDate}` : undefined,
      coverage.status === "pending_verification" ? "pending verification" : undefined,
    ],
    " · ",
  );
}

/** One identity or coverage answer beside the chart. */
export interface IntakeIdentityRow {
  readonly field: IntakeDemographicField;
  readonly label: string;
  readonly answer: string;
  /** Undefined until a chart is linked; "" when the chart holds nothing. */
  readonly chart: string | undefined;
  /** The chart already says the same: nothing to apply. */
  readonly same: boolean;
  /** Ticked when the review opens. Riskier changes start unticked and say why. */
  readonly defaultApply: boolean;
  readonly caution?: string | undefined;
}

function coverageRow(
  coverage: PatientQuestionnaireCoverage,
  demographics: PatientDemographics | undefined,
): IntakeIdentityRow {
  const answer = questionnaireCoverageLine(coverage);
  const base = { field: "coverage", label: INTAKE_FIELD_LABELS.coverage, answer } as const;
  if (demographics === undefined) {
    return { ...base, chart: undefined, same: false, defaultApply: false };
  }
  const chart = demographics.coverage;
  const chartLine = chartCoverageLine(demographics);
  if (coverage.kind === "cash") {
    return chart === undefined
      ? { ...base, chart: chartLine, same: true, defaultApply: false }
      : {
          ...base,
          chart: chartLine,
          same: false,
          defaultApply: false,
          caution: "Applying this ends every plan on the chart.",
        };
  }
  if (chart === undefined) {
    return { ...base, chart: chartLine, same: false, defaultApply: true };
  }
  const ohip = coverage.payerName === OHIP_PAYER_NAME;
  const sameNumber = ohip
    ? digits(coverage.memberId) === digits(chart.memberId)
    : plain(coverage.memberId).replace(/[^a-z0-9]/gu, "") ===
        plain(chart.memberId).replace(/[^a-z0-9]/gu, "") && coverage.payerName === chart.payerName;
  if (!sameNumber) {
    return {
      ...base,
      chart: chartLine,
      same: false,
      defaultApply: false,
      caution: "Replaces the plan on the chart. Check the card first.",
    };
  }
  const versionCode = ohip && "versionCode" in coverage ? coverage.versionCode : undefined;
  const expiryDate = ohip && "expiryDate" in coverage ? coverage.expiryDate : undefined;
  const same =
    (versionCode === undefined || versionCode === chart.versionCode) &&
    (expiryDate === undefined || expiryDate === chart.expiryDate);
  return { ...base, chart: chartLine, same, defaultApply: !same };
}

function textRow(
  field: IntakeDemographicField,
  answer: string,
  chart: string | undefined,
  options: { readonly compare?: (left: string, right: string) => boolean; caution?: string } = {},
): IntakeIdentityRow {
  const compare = options.compare ?? ((left, right) => left.trim() === right.trim());
  const same = chart !== undefined && compare(answer, chart);
  return {
    field,
    label: INTAKE_FIELD_LABELS[field],
    answer,
    chart,
    same,
    defaultApply: chart !== undefined && !same && options.caution === undefined,
    ...(options.caution === undefined || same ? {} : { caution: options.caution }),
  };
}

/**
 * Every identity and coverage answer the patient gave, beside the linked chart's value (or
 * undefined before a chart is linked). Answers left blank are not listed: applying a blank is
 * refused. The date of birth is never applied (a linked chart already has the booking's).
 */
export function intakeIdentityRows(
  answers: SchedulingIntakeAdministrativeAnswers,
  demographics: PatientDemographics | undefined,
): IntakeIdentityRow[] {
  const rows: IntakeIdentityRow[] = [];
  const identity = answers.identity;
  for (const field of INTAKE_DEMOGRAPHIC_FIELDS) {
    if (field === "coverage") {
      rows.push(coverageRow(answers.coverage, demographics));
      continue;
    }
    if (identity === null) continue;
    switch (field) {
      case "administrativeSex": {
        const chart = demographics?.administrativeSex;
        const row = textRow(
          field,
          SEX_LABELS[identity.administrativeSex] ?? identity.administrativeSex,
          chart === undefined ? undefined : (SEX_LABELS[chart] ?? chart),
        );
        // "Prefer not to say" never overwrites a recorded sex without staff choosing it.
        rows.push(
          identity.administrativeSex === "unknown" && chart !== undefined && chart !== "unknown"
            ? {
                ...row,
                defaultApply: false,
                caution: "The patient preferred not to say; the chart has a recorded sex.",
              }
            : row,
        );
        break;
      }
      case "address": {
        if (identity.address === undefined) break;
        rows.push(
          textRow(
            field,
            addressLine(identity.address),
            demographics === undefined ? undefined : addressLine(demographics.address),
            {
              compare: (left, right) => plain(left) === plain(right),
              caution: "Replaces the whole address on the chart.",
            },
          ),
        );
        break;
      }
      case "emergencyContact": {
        if (identity.emergencyContact === undefined) break;
        rows.push(
          textRow(
            field,
            contactLine(identity.emergencyContact),
            demographics === undefined ? undefined : contactLine(demographics.emergencyContact),
            {
              compare: (left, right) => plain(left) === plain(right),
              caution: "Replaces the whole emergency contact on the chart.",
            },
          ),
        );
        break;
      }
      case "phone": {
        rows.push(
          textRow(
            field,
            identity.phone,
            demographics === undefined ? undefined : (demographics.phone ?? ""),
            {
              compare: (left, right) => digits(left) !== "" && digits(left) === digits(right),
            },
          ),
        );
        break;
      }
      case "email": {
        if (identity.email === undefined) break;
        rows.push(
          textRow(
            field,
            identity.email,
            demographics === undefined ? undefined : (demographics.email ?? ""),
            {
              compare: (left, right) => plain(left) === plain(right),
            },
          ),
        );
        break;
      }
      default: {
        const value = identity[field];
        if (value === undefined) break;
        rows.push(
          textRow(
            field,
            value,
            demographics === undefined ? undefined : (demographics[field] ?? ""),
          ),
        );
      }
    }
  }
  return rows;
}

// ---------------------------------------------------------------------------
// The Details step: what differs from the chart, and what already matches
// ---------------------------------------------------------------------------

/** One detail as Review intake's Details step lists it. */
export interface IntakeDetailLine {
  /** The demographic field, or "dateOfBirth" (never applied). */
  readonly key: IntakeDemographicField | "dateOfBirth";
  readonly label: string;
  readonly answer: string;
  /** The chart's value ("" when it holds nothing); undefined before a chart is linked. */
  readonly chart: string | undefined;
  readonly same: boolean;
  /** The field an apply would copy; absent for a detail decided by an earlier rule. */
  readonly field?: IntakeDemographicField | undefined;
  /** "Use patient's" is chosen when the review opens (intakeIdentityRows' rule). */
  readonly defaultApply: boolean;
  readonly caution?: string | undefined;
  /** Decided by an earlier rule, in two or three words. */
  readonly decided?: string | undefined;
  /** Checks staff make on this detail: the health card's follow-ups, on the coverage. */
  readonly warnings: readonly string[];
}

export interface IntakeDetailReview {
  /** Every detail the patient gave, in order (shown read-only before a chart is linked). */
  readonly lines: readonly IntakeDetailLine[];
  /** Once linked: what differs from the chart or carries a check. Each is shown with its choice. */
  readonly attention: readonly IntakeDetailLine[];
  /** Once linked: what the chart already says. Collapsed into one line. */
  readonly matching: readonly IntakeDetailLine[];
}

/**
 * The Details step (PA-6): the identity and coverage answers beside the chart, split into what
 * needs a decision or a check and what already matches. The date of birth is listed but never
 * applied (the link checked it, and a linked chart has the booking's); the health card's
 * follow-ups sit on the coverage, so a matching card that still needs a check stays in view.
 */
export function intakeDetailReview(
  answers: SchedulingIntakeAdministrativeAnswers,
  demographics: PatientDemographics | undefined,
  followUps: readonly HealthCardFollowUp[],
): IntakeDetailReview {
  const lines: IntakeDetailLine[] = [];
  if (answers.identity !== null) {
    const chart = demographics?.dateOfBirth;
    lines.push({
      key: "dateOfBirth",
      label: "Date of birth",
      answer: answers.identity.dateOfBirth,
      chart,
      same: chart === answers.identity.dateOfBirth,
      defaultApply: false,
      decided: "Checked at link",
      warnings: [],
    });
  }
  const cardWarnings = followUps.map((followUp) => HEALTH_CARD_FOLLOW_UP_LABELS[followUp]);
  for (const row of intakeIdentityRows(answers, demographics)) {
    lines.push({
      key: row.field,
      label: row.label,
      answer: row.answer,
      chart: row.chart,
      same: row.same,
      field: row.field,
      defaultApply: row.defaultApply,
      caution: row.caution,
      warnings: row.field === "coverage" ? cardWarnings : [],
    });
  }
  if (demographics === undefined) return { lines, attention: [], matching: [] };
  return {
    lines,
    attention: lines.filter((line) => !line.same || line.warnings.length > 0),
    matching: lines.filter((line) => line.same && line.warnings.length === 0),
  };
}

/** A detail offered as "Use patient's" / "Keep chart's": it differs and the chart can take it. */
export function isDetailChoice(
  line: IntakeDetailLine,
): line is IntakeDetailLine & { readonly field: IntakeDemographicField } {
  return line.field !== undefined && !line.same && line.chart !== undefined;
}

const ARRIVED_STATUSES: ReadonlySet<string> = new Set(["checked_in", "in_progress", "completed"]);

/** What the Details step shows for the reason for visit. */
export type IntakeReasonDecision =
  /** The booking already has the patient's words. */
  | { readonly kind: "hidden" }
  /** "Use patient's" / "Keep booking's", before arrival. */
  | { readonly kind: "choice"; readonly booking: string }
  /** Shown for reading only, with why in two or three words. */
  | {
      readonly kind: "info";
      readonly booking?: string | undefined;
      readonly decided?: string | undefined;
      readonly warning?: string | undefined;
    };

/**
 * The reason for visit beside the booking's: it can become the booking reason only before
 * arrival, while the review is open, and when it fits (check-in copies the booking reason to the
 * chief complaint). A walk-in's chief complaint is the desk's reason, so its answer is read only.
 */
export function intakeReasonDecision(input: {
  readonly reason: string;
  readonly booking: { readonly reason: string; readonly status: string } | undefined;
  readonly walkIn: boolean;
  /** The review is open and the booking's version is known, so an apply can be sent. */
  readonly canApply: boolean;
}): IntakeReasonDecision {
  if (input.walkIn) return { kind: "info", decided: "Desk's reason used" };
  const booking = input.booking;
  if (booking === undefined) return { kind: "info" };
  if (booking.reason.trim() === input.reason.trim()) return { kind: "hidden" };
  if (ARRIVED_STATUSES.has(booking.status)) {
    return { kind: "info", booking: booking.reason, decided: "Set at check-in" };
  }
  if (!["booked", "confirmed"].includes(booking.status) || !input.canApply) {
    return { kind: "info", booking: booking.reason };
  }
  if (!intakeReasonFitsBooking(input.reason)) {
    return {
      kind: "info",
      booking: booking.reason,
      warning: `Over ${SCHEDULING_BOOKING_REASON_MAX_LENGTH} characters: edit the reason in Manage appointment.`,
    };
  }
  return { kind: "choice", booking: booking.reason };
}

/** A section as the finish button names it. */
const MARK_WORDS: Readonly<Record<SchedulingIntakeSection, string>> = {
  administrative: "details",
  clinical: "health answers",
};

/**
 * The finish button's words: exactly what one click does, naming the section it marks (two
 * sections read "both", and the Finish step lists them by name). Null when there is nothing left
 * to do (no change chosen and no section to mark).
 */
export function intakeFinishLabel(
  changes: number,
  marks: readonly SchedulingIntakeSection[],
): string | null {
  const [only] = marks;
  const mark =
    only === undefined
      ? undefined
      : marks.length === 1
        ? `mark ${MARK_WORDS[only]} reviewed`
        : "mark both reviewed";
  if (changes > 0) {
    const apply = `Apply ${changes} ${changes === 1 ? "change" : "changes"}`;
    return mark === undefined ? apply : `${apply} and ${mark}`;
  }
  return mark === undefined ? null : `${mark.charAt(0).toUpperCase()}${mark.slice(1)}`;
}

// ---------------------------------------------------------------------------
// Health answers that can be added to the chart
// ---------------------------------------------------------------------------

export const CHART_ENTRY_SECTION_LABELS: Readonly<Record<IntakeChartEntrySection, string>> = {
  allergies: "Allergies",
  medications: "Medications",
  pmh: "Medical history",
};

/** One part of the patient's answer that could become a chart entry. */
export interface IntakeEntryProposal {
  readonly display: string;
  /** The chart already has an entry of that name, not removed: it would not be added again. */
  readonly onChart: boolean;
  /**
   * When that entry is on the chart with a status other than active (a resolved allergy or
   * history entry, a completed medication): how the chart shows it, so staff can change it there.
   */
  readonly chartStatus?: string | undefined;
}

export interface IntakeEntryProposals {
  readonly answer: string;
  readonly entries: readonly IntakeEntryProposal[];
  readonly tooLong: readonly string[];
  readonly noneAnswers: readonly string[];
}

const nameKey = (display: string) => display.trim().toLocaleLowerCase();

/** How the chart shows an entry that is on it but not active. */
const CHART_STATUS_WORDS: Readonly<Record<string, string>> = {
  resolved: "resolved",
  completed: "stopped",
  unknown: "status unknown",
};

/**
 * The parts of one health answer, each marked when the chart already has it by name. This is
 * the server's own rule (api.apply_scheduling_intake_chart_entries), so the count staff see is
 * the count that is added: an entry on the chart in any status but removed (inactive) counts;
 * a removed one does not, as the chart no longer shows it, and applying makes it active again.
 */
export function intakeEntryProposals(
  section: IntakeChartEntrySection,
  answers: SchedulingIntakeClinicalAnswers,
  chartItems: readonly { readonly display: string; readonly status?: string }[] | undefined,
): IntakeEntryProposals {
  const answer = answers[INTAKE_CHART_ENTRY_ANSWER[section]];
  const parts = splitIntakeAnswer(answer);
  const onChart = new Map<string, string | undefined>();
  for (const item of chartItems ?? []) {
    if (item.status === "inactive") continue;
    const key = nameKey(item.display);
    // An active entry of the name wins over another status.
    if (!onChart.has(key) || item.status === "active") onChart.set(key, item.status);
  }
  return {
    answer,
    entries: parts.entries.map((display) => {
      const key = nameKey(display);
      if (!onChart.has(key)) return { display, onChart: false };
      const status = onChart.get(key);
      const chartStatus = status === undefined ? undefined : CHART_STATUS_WORDS[status];
      return chartStatus === undefined
        ? { display, onChart: true }
        : { display, onChart: true, chartStatus };
    }),
    tooLong: parts.tooLong,
    noneAnswers: parts.noneAnswers,
  };
}

// ---------------------------------------------------------------------------
// Words for the rest of the review
// ---------------------------------------------------------------------------

export const PREGNANCY_LABELS: Readonly<
  Record<SchedulingIntakeClinicalAnswers["pregnant"], string>
> = {
  yes: "Yes",
  no: "No",
  "not-applicable": "Not applicable",
  "prefer-not-to-say": "Prefer not to say",
};

export const HEALTH_CARD_FOLLOW_UP_LABELS: Readonly<Record<HealthCardFollowUp, string>> = {
  version_code_not_given: "Version code not given: check the card",
  expiry_not_given: "Expiry date not given: check the card",
  card_expired: "Card has expired: ask for a current card",
};

const APPLICATION_LABELS: Readonly<
  Record<Exclude<SchedulingIntakeApplicationKind, "demographics" | IntakeChartEntrySection>, string>
> = {
  chart_linked: "Linked the chart",
  chart_created: "Created the chart",
  chart_mismatch: "Recorded that no listed chart is this patient",
  reason: "Made the reason the booking reason",
};

const ENTRY_NOUNS: Readonly<Record<IntakeChartEntrySection, readonly [string, string]>> = {
  allergies: ["allergy", "allergies"],
  medications: ["medication", "medications"],
  pmh: ["history entry", "history entries"],
};

const entryCount = (section: IntakeChartEntrySection, count: number) => {
  const [one, many] = ENTRY_NOUNS[section];
  return `${count} ${count === 1 ? one : many}`;
};

/**
 * What the finish button's chart-entry applies did, in one short line: "Added as
 * patient-reported: 2 allergies, 1 medication. Medical history: nothing new." Undefined when no
 * entries were sent.
 */
export function intakeEntriesNote(
  results: readonly { readonly section: IntakeChartEntrySection; readonly added: number }[],
): string | undefined {
  const added = results.filter((result) => result.added > 0);
  const none = results.filter((result) => result.added === 0);
  const parts = [
    added.length === 0
      ? undefined
      : `Added as patient-reported: ${added
          .map((result) => entryCount(result.section, result.added))
          .join(", ")}.`,
    ...none.map((result) => `${CHART_ENTRY_SECTION_LABELS[result.section]}: nothing new.`),
  ].filter((part) => part !== undefined);
  return parts.length === 0 ? undefined : parts.join(" ");
}

/**
 * What one apply did, as "Applied so far" lists it, counted as it reached the chart: an apply
 * that added nothing new (every entry was already there) or changed no detail says so, rather
 * than reading as if something was written.
 */
export function applicationLabel(application: {
  readonly kind: SchedulingIntakeApplicationKind;
  readonly itemCount: number;
}): string {
  const { kind, itemCount } = application;
  switch (kind) {
    case "demographics":
      return itemCount === 0
        ? "Applied details: nothing changed"
        : `Applied ${itemCount} ${itemCount === 1 ? "detail" : "details"}`;
    case "allergies":
    case "medications":
    case "pmh": {
      const [one, many] = ENTRY_NOUNS[kind];
      return itemCount === 0
        ? `Added no new ${many}: already on the chart`
        : `Added ${itemCount} ${itemCount === 1 ? one : many}`;
    }
    default:
      return APPLICATION_LABELS[kind];
  }
}

/** A province code as its name; codes outside the list are shown as they are. */
export function provinceName(code: string): string {
  return (CANADIAN_PROVINCE_NAMES as Readonly<Record<string, string>>)[code] ?? code;
}

/** "Reviewed by Dr. Chen", "To review" or "Not reviewed (review closed)". */
export function sectionReviewLabel(review: SchedulingIntakeSectionReview): string {
  switch (review.state) {
    case "reviewed":
      return `Reviewed by ${review.reviewedByName ?? "a staff member"}`;
    case "awaiting_review":
      return "To review";
    case "expired":
      return "Not reviewed (review closed)";
  }
}
